/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, query, where, getDocs, limit, getDoc, doc } from 'firebase/firestore';
import { VIBES } from './MoodTags';

// ─────────────────────────────────────────────────────────────────────────────
// WrappedAnual — resumen anual de lectura estilo Spotify Wrapped
//
// Props:
//   userId    — UID del usuario
//   nombre    — nombre del usuario
//   esPremium — boolean
//   onUpgrade — función para abrir modal premium
//
// Fuentes de datos (solo lectura, colecciones ya existentes):
//   estados_lectura  → libros leídos este año, vibes, géneros
//   sesiones_lectura → tiempo total de lectura
//   valoraciones     → media de estrellas
//   stats_timer      → tiempo total acumulado
// ─────────────────────────────────────────────────────────────────────────────

const ANIO = new Date().getFullYear();
const MESES_CORTO = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

function calcularRachaMasLarga(estados) {
  if (!estados.length) return 0;
  const dias = [...new Set(
    estados.map(e => {
      const d = e.fecha?.toDate?.();
      if (!d) return null;
      const dia = new Date(d); dia.setHours(0,0,0,0);
      return dia.getTime();
    }).filter(Boolean)
  )].sort((a,b) => a - b);

  let maxRacha = 1, racha = 1;
  for (let i = 1; i < dias.length; i++) {
    if (dias[i] - dias[i-1] === 86400000) { racha++; maxRacha = Math.max(maxRacha, racha); }
    else racha = 1;
  }
  return maxRacha;
}

function formatearTiempo(seg) {
  if (!seg) return '0m';
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ── Tarjeta individual del wrapped ───────────────────────────────────────────
function TarjetaWrapped({ icono, titulo, valor, sublabel, color = '#1DB954', delay = 0 }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <div style={{
      background: '#0d0d0d',
      border: `1px solid ${color}22`,
      borderRadius: '16px',
      padding: '18px 16px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(12px)',
      transition: 'opacity 0.5s ease, transform 0.5s ease',
    }}>
      <div style={{ position: 'absolute', top: '-20px', left: '50%', transform: 'translateX(-50%)', width: '60px', height: '60px', background: color, borderRadius: '50%', filter: 'blur(28px)', opacity: 0.15 }} />
      <div style={{ fontSize: '22px', marginBottom: '8px' }}>{icono}</div>
      <p style={{ margin: '0 0 4px', fontSize: '28px', fontWeight: 900, color, letterSpacing: '-1px', lineHeight: 1 }}>{valor}</p>
      <p style={{ margin: '0 0 2px', fontSize: '12px', color: 'white', fontWeight: 'bold' }}>{titulo}</p>
      {sublabel && <p style={{ margin: 0, fontSize: '11px', color: '#444' }}>{sublabel}</p>}
    </div>
  );
}

// ── Barra de mes ──────────────────────────────────────────────────────────────
function BarraMes({ mes, cantidad, maximo, delay = 0 }) {
  const [ancho, setAncho] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setAncho(maximo > 0 ? (cantidad / maximo) * 100 : 0), delay + 300);
    return () => clearTimeout(t);
  }, [cantidad, maximo, delay]);

  if (cantidad === 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
      <span style={{ fontSize: '11px', color: '#444', width: '28px', textAlign: 'right', flexShrink: 0 }}>{MESES_CORTO[mes]}</span>
      <div style={{ flex: 1, height: '6px', background: '#1a1a1a', borderRadius: '99px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${ancho}%`, background: 'linear-gradient(90deg, #1DB95488, #1DB954)', borderRadius: '99px', transition: '0.8s cubic-bezier(0.4,0,0.2,1)' }} />
      </div>
      <span style={{ fontSize: '11px', color: '#1DB954', fontWeight: 'bold', width: '16px', flexShrink: 0 }}>{cantidad}</span>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export function WrappedAnual({ userId, nombre, esPremium, onUpgrade }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [expandido, setExpandido] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let activo = true;
    const cargar = async () => {
      setCargando(true);
      try {
        const inicioAnio = new Date(ANIO, 0, 1);

        // ── Libros leídos este año ──
        const estadosSnap = await getDocs(
          query(collection(db, 'estados_lectura'), where('userId', '==', userId), where('estado', '==', 'leido'))
        );
        const todosLeidos = estadosSnap.docs.map(d => d.data());
        const leidosAnio = todosLeidos.filter(e => {
          const f = e.fecha?.toDate?.();
          return f && f >= inicioAnio;
        });

        // ── Libros por mes ──
        const porMes = Array(12).fill(0);
        leidosAnio.forEach(e => {
          const f = e.fecha?.toDate?.();
          if (f) porMes[f.getMonth()]++;
        });

        // ── Mes más lector ──
        const maxLibrosMes = Math.max(...porMes);
        const mesMasLector = porMes.indexOf(maxLibrosMes);

        // ── Vibes más usadas ──
        const vibesCount = {};
        leidosAnio.forEach(e => {
          (e.vibes || []).forEach(v => { vibesCount[v] = (vibesCount[v] || 0) + 1; });
        });
        const topVibes = Object.entries(vibesCount).sort((a,b) => b[1]-a[1]).slice(0, 3)
          .map(([id, n]) => ({ ...VIBES.find(v => v.id === id), count: n })).filter(Boolean);

        // ── Racha más larga ──
        const actSnap = await getDocs(
          query(collection(db, 'actividad_reciente'), where('userId', '==', userId), limit(365))
        );
        const actividad = actSnap.docs.map(d => d.data()).filter(a => {
          const f = a.fecha?.toDate?.();
          return f && f >= inicioAnio;
        });
        const rachaMasLarga = calcularRachaMasLarga(actividad);

        // ── Tiempo total leyendo ──
        let tiempoTotal = 0;
        try {
          const statsSnap = await getDoc(doc(db, 'stats_timer', userId));
          if (statsSnap.exists()) tiempoTotal = statsSnap.data().totalSegundos || 0;
        } catch {}

        // ── Valoración media ──
        const valSnap = await getDocs(
          query(collection(db, 'valoraciones'), where('userId', '==', userId))
        );
        const valoraciones = valSnap.docs.map(d => d.data());
        const valAnio = valoraciones.filter(v => {
          const f = v.fecha?.toDate?.();
          return f && f >= inicioAnio;
        });
        const mediaVotos = valAnio.length > 0
          ? (valAnio.reduce((a,b) => a + (b.estrellas || 0), 0) / valAnio.length).toFixed(1)
          : null;

        // ── Autor más leído ──
        const autoresCount = {};
        leidosAnio.forEach(e => {
          if (e.libro?.autor) autoresCount[e.libro.autor] = (autoresCount[e.libro.autor] || 0) + 1;
        });
        const topAutor = Object.entries(autoresCount).sort((a,b) => b[1]-a[1])[0];

        // ── Páginas estimadas ──
        const paginasEstimadas = leidosAnio.length * 300;

        if (activo) setDatos({
          leidosAnio: leidosAnio.length,
          porMes,
          maxLibrosMes,
          mesMasLector,
          topVibes,
          rachaMasLarga,
          tiempoTotal,
          mediaVotos,
          topAutor: topAutor ? { autor: topAutor[0], cantidad: topAutor[1] } : null,
          paginasEstimadas,
        });
      } catch (e) { console.error(e); }
      if (activo) setCargando(false);
    };
    cargar();
    return () => { activo = false; };
  }, [userId]);

  if (cargando) return (
    <div style={{ height: '80px', background: '#0d0d0d', borderRadius: '16px', animation: 'pulse 1.5s infinite', marginBottom: '16px' }} />
  );

  if (!datos || datos.leidosAnio === 0) return (
    <div style={{ background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: '16px', padding: '20px', marginBottom: '16px', textAlign: 'center' }}>
      <p style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 900, color: 'white' }}>Tu Wrapped {ANIO}</p>
      <p style={{ margin: 0, fontSize: '13px', color: '#333' }}>Marca libros como leídos y aquí aparecerá tu resumen del año.</p>
    </div>
  );

  const primerNombre = (nombre || '').split(' ')[0] || 'Lector';

  return (
    <div style={{ marginBottom: '16px' }}>
      {/* Cabecera — siempre visible */}
      <div
        onClick={() => setExpandido(v => !v)}
        style={{
          background: expandido
            ? 'linear-gradient(135deg, #0a1f0a 0%, #0d0d0d 100%)'
            : 'linear-gradient(135deg, #0a1a0a 0%, #111 100%)',
          border: '1px solid #1DB95433',
          borderRadius: expandido ? '16px 16px 0 0' : '16px',
          padding: '18px 20px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: '0.2s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#1DB95422', border: '1px solid #1DB95444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#1DB954">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14l-5-5 1.41-1.41L12 14.17l7.59-7.59L21 8l-9 9z"/>
            </svg>
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '15px', fontWeight: 900, color: 'white' }}>Tu Wrapped {ANIO}</p>
            <p style={{ margin: 0, fontSize: '12px', color: '#1DB954' }}>
              {datos.leidosAnio} {datos.leidosAnio === 1 ? 'libro leído' : 'libros leídos'} · {formatearTiempo(datos.tiempoTotal)} leyendo
            </p>
          </div>
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="#444" style={{ transform: expandido ? 'rotate(180deg)' : 'none', transition: '0.2s', flexShrink: 0 }}>
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
        </svg>
      </div>

      {/* Contenido expandible */}
      {expandido && (
        <div style={{ background: '#0d0d0d', border: '1px solid #1DB95422', borderTop: 'none', borderRadius: '0 0 16px 16px', padding: '20px' }}>

          {/* Título personalizado */}
          <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#555', textAlign: 'center' }}>
            {primerNombre}, este ha sido tu año como lector
          </p>

          {/* Stats principales — 2x2 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
            <TarjetaWrapped
              icono={<svg width="22" height="22" viewBox="0 0 24 24" fill="#1DB954"><path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9H7V9h10v2zm0-4H7V5h10v2z"/></svg>}
              titulo="Libros leídos"
              valor={datos.leidosAnio}
              sublabel={`~${(datos.paginasEstimadas / 1000).toFixed(1)}k páginas`}
              color="#1DB954"
              delay={0}
            />
            <TarjetaWrapped
              icono={<svg width="22" height="22" viewBox="0 0 24 24" fill="#f59e0b"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/></svg>}
              titulo="Tiempo leyendo"
              valor={formatearTiempo(datos.tiempoTotal)}
              sublabel="con el timer activo"
              color="#f59e0b"
              delay={100}
            />
            {datos.rachaMasLarga > 0 && (
              <TarjetaWrapped
                icono={<svg width="22" height="22" viewBox="0 0 24 24" fill="#f97316"><path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z"/></svg>}
                titulo="Racha más larga"
                valor={`${datos.rachaMasLarga}d`}
                sublabel="días consecutivos"
                color="#f97316"
                delay={200}
              />
            )}
            {datos.mediaVotos && (
              <TarjetaWrapped
                icono={<svg width="22" height="22" viewBox="0 0 24 24" fill="#eab308"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>}
                titulo="Tu valoración media"
                valor={`${datos.mediaVotos}★`}
                sublabel="este año"
                color="#eab308"
                delay={300}
              />
            )}
          </div>

          {/* Autor favorito */}
          {datos.topAutor && datos.topAutor.cantidad > 1 && (
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: '12px', padding: '14px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ margin: '0 0 2px', fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }}>Autor favorito</p>
                <p style={{ margin: 0, fontSize: '16px', fontWeight: 900, color: 'white' }}>{datos.topAutor.autor}</p>
              </div>
              <span style={{ background: '#1DB95422', color: '#1DB954', fontSize: '13px', fontWeight: 'bold', padding: '4px 12px', borderRadius: '20px', border: '1px solid #1DB95444' }}>
                {datos.topAutor.cantidad} libros
              </span>
            </div>
          )}

          {/* Mes más lector */}
          {datos.maxLibrosMes > 0 && (
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: '12px', padding: '14px 16px', marginBottom: '12px' }}>
              <p style={{ margin: '0 0 12px', fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }}>Libros por mes</p>
              {datos.porMes.map((n, i) => (
                <BarraMes key={i} mes={i} cantidad={n} maximo={datos.maxLibrosMes} delay={i * 40} />
              ))}
            </div>
          )}

          {/* Top vibes — solo premium o si tiene alguna */}
          {datos.topVibes.length > 0 && (
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: '12px', padding: '14px 16px', marginBottom: '12px' }}>
              <p style={{ margin: '0 0 10px', fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }}>Tus vibes favoritas</p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {datos.topVibes.map((v, i) => (
                  <span key={v.id} style={{
                    background: i === 0 ? '#1DB95422' : '#0a0a0a',
                    color: i === 0 ? '#1DB954' : '#555',
                    border: `1px solid ${i === 0 ? '#1DB95444' : '#2a2a2a'}`,
                    borderRadius: '20px',
                    padding: '6px 14px',
                    fontSize: '13px',
                    fontWeight: i === 0 ? 'bold' : 'normal',
                  }}>
                    {v.label} {i === 0 && '·1'}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Muro premium para compartir */}
          {!esPremium ? (
            <div style={{ background: 'linear-gradient(135deg, #1a0533, #1a1a2e)', border: '1px solid #a78bfa33', borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <div>
                <p style={{ margin: '0 0 2px', fontSize: '13px', fontWeight: 'bold', color: 'white' }}>✦ Wrapped avanzado</p>
                <p style={{ margin: 0, fontSize: '12px', color: '#7c5fad' }}>Comparativa con {ANIO - 1}, tarjeta compartible y más</p>
              </div>
              <button onClick={onUpgrade} style={{ background: 'linear-gradient(135deg, #6a3fa0, #a78bfa)', color: 'white', border: 'none', borderRadius: '20px', padding: '8px 16px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}>
                Premium
              </button>
            </div>
          ) : (
            <WrappedPremium datos={datos} nombre={primerNombre} />
          )}
        </div>
      )}
    </div>
  );
}

// ── Sección premium del Wrapped ───────────────────────────────────────────────
function WrappedPremium({ datos, nombre }) {
  const [compartiendo, setCompartiendo] = useState(false);

  const generarTextoCompartir = () => {
    const lineas = [
      `📚 Mi Wrapped ${ANIO} en Leemos`,
      ``,
      `📖 ${datos.leidosAnio} libros leídos`,
      datos.tiempoTotal > 0 ? `⏱️ ${formatearTiempo(datos.tiempoTotal)} leyendo` : null,
      datos.rachaMasLarga > 1 ? `🔥 Racha de ${datos.rachaMasLarga} días` : null,
      datos.topAutor?.cantidad > 1 ? `✍️ Autor favorito: ${datos.topAutor.autor}` : null,
      datos.topVibes.length > 0 ? `🎭 Vibe: ${datos.topVibes.map(v => v.label).join(', ')}` : null,
      ``,
      `#Leemos #WrappedLector #Lectura`,
    ].filter(l => l !== null);

    return lineas.join('\n');
  };

  const compartir = async () => {
    const texto = generarTextoCompartir();
    setCompartiendo(true);
    try {
      if (navigator.share) {
        await navigator.share({ text: texto });
      } else {
        await navigator.clipboard.writeText(texto);
        alert('¡Copiado al portapapeles!');
      }
    } catch {}
    setCompartiendo(false);
  };

  return (
    <div style={{ background: 'linear-gradient(135deg, #0a1a0a, #111)', border: '1px solid #1DB95433', borderRadius: '12px', padding: '16px' }}>
      <p style={{ margin: '0 0 12px', fontSize: '11px', color: '#1DB954', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }}>✦ Premium</p>
      <button
        onClick={compartir}
        disabled={compartiendo}
        style={{ width: '100%', background: '#1DB954', color: 'black', border: 'none', borderRadius: '25px', padding: '12px', fontWeight: 900, cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: '0.2s' }}
        onMouseEnter={e => e.currentTarget.style.background = '#1ed760'}
        onMouseLeave={e => e.currentTarget.style.background = '#1DB954'}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z"/></svg>
        {compartiendo ? 'Compartiendo...' : `Compartir mi Wrapped ${ANIO}`}
      </button>
    </div>
  );
}

// ── WrapMensual — resumen mensual compartible (no premium) ───────────────────
export function WrapMensual({ userId, nombre }) {
  const [datos, setDatos] = useState(null);
  const [compartiendo, setCompartiendo] = useState(false);

  const mesActual = new Date().getMonth();
  const anioActual = new Date().getFullYear();
  const nombreMes = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'][mesActual];

  useEffect(() => {
    if (!userId) return;
    const cargar = async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'estados_lectura'),
          where('userId', '==', userId),
          where('estado', '==', 'leido')
        ));
        const inicioMes = new Date(anioActual, mesActual, 1).getTime();
        const finMes = new Date(anioActual, mesActual + 1, 0, 23, 59, 59).getTime();

        const leidos = snap.docs
          .map(d => d.data())
          .filter(d => {
            const f = d.fecha?.toDate?.()?.getTime();
            return f && f >= inicioMes && f <= finMes;
          });

        // Género más leído este mes
        const generos = {};
        leidos.forEach(d => { if (d.libro?.genero) generos[d.libro.genero] = (generos[d.libro.genero] || 0) + 1; });
        const topGenero = Object.entries(generos).sort((a,b) => b[1]-a[1])[0]?.[0] || null;

        // Páginas estimadas
        const paginas = leidos.reduce((acc, d) => acc + (d.totalPaginas || 300), 0);

        // Racha del mes (días con actividad)
        const statsSnap = await getDocs(query(collection(db, 'stats_timer'), where('__name__', '==', userId)));
        const totalSeg = statsSnap.docs[0]?.data()?.totalSegundos || 0;

        setDatos({ leidos: leidos.length, paginas, topGenero, horas: Math.round(totalSeg / 3600) });
      } catch (e) { console.error(e); }
    };
    cargar();
  }, [userId]);

  const compartir = async () => {
    if (!datos) return;
    setCompartiendo(true);
    const texto = [
      `📚 Mi ${nombreMes} en Leemos`,
      ``,
      `📖 ${datos.leidos} libro${datos.leidos !== 1 ? 's' : ''} leído${datos.leidos !== 1 ? 's' : ''}`,
      `📄 ~${datos.paginas.toLocaleString('es')} páginas`,
      datos.topGenero ? `🎭 Género: ${datos.topGenero}` : null,
      datos.horas > 0 ? `⏱️ ${datos.horas}h de lectura acumuladas` : null,
      ``,
      `#Leemos #LecturaEnEspañol #${nombreMes}Lector`,
    ].filter(Boolean).join('\n');
    try {
      if (navigator.share) await navigator.share({ text: texto });
      else { await navigator.clipboard.writeText(texto); alert('¡Copiado al portapapeles!'); }
    } catch {}
    setCompartiendo(false);
  };

  if (!datos || datos.leidos === 0) return null;

  return (
    <div>
      {/* Cabecera igual que otras secciones del feed */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '17px', fontWeight: 900, margin: 0, color: 'white', letterSpacing: '-0.2px' }}>{nombreMes} en números</h2>
        <button onClick={compartir} disabled={compartiendo}
          style={{ background: 'none', border: 'none', color: '#1DB954', fontSize: '12px', cursor: 'pointer', fontWeight: 900, padding: 0, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px', transition: '0.2s' }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
          {compartiendo ? 'Compartiendo...' : 'Compartir'}
          <svg width="13" height="13" viewBox="0 0 24 24" fill="#1DB954"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L7.04 9.81C6.5 9.31 5.79 9 5 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/></svg>
        </button>
      </div>
      {/* Stats — mismo estilo que "Tu progreso este año" */}
      <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', scrollbarWidth: 'none' }}>
        {[
          { valor: datos.leidos, label: 'Libros leídos', color: '#1DB954', bg: 'linear-gradient(135deg, #1DB95422, #1DB95408)', iconPath: 'M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9H9V9h10v2zm-4 4H9v-2h6v2zm4-8H9V5h10v2z' },
          { valor: `~${Math.round(datos.paginas / 100) * 100}`, label: 'Páginas', color: '#a78bfa', bg: 'linear-gradient(135deg, #a78bfa22, #a78bfa08)', iconPath: 'M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z' },
          ...(datos.topGenero ? [{ valor: datos.topGenero, label: 'Género del mes', color: '#f59e0b', bg: 'linear-gradient(135deg, #f59e0b22, #f59e0b08)', iconPath: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' }] : []),
        ].map(({ valor, label, color, bg, iconPath }) => (
          <div key={label} style={{ flex: '1 1 0', minWidth: '96px', background: `radial-gradient(circle at 30% 20%, ${color}35, #0a0e16)`, borderRadius: '18px', border: `1px solid ${color}2a`, padding: '20px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: '50%', background: `${color}20`, border: `1px solid ${color}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 16px ${color}40` }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill={color}><path d={iconPath}/></svg>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: typeof valor === 'number' ? '30px' : '16px', fontWeight: 900, color, lineHeight: 1, letterSpacing: '-0.5px' }}>{valor}</p>
              <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#5a6b5f', lineHeight: 1.3, fontWeight: 600 }}>{label}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
